/**
 * The docked artifact side panel: square paper on the right of the conversation, ~40rem, left-edge drag resize.
 * Header = ellipsized title + Copy-DATA + Expand + Close. The artifact renders directly on the surface (no card
 * wrapper). Expand fills the conversation area (`position:absolute; inset:0`; the region parent is relative).
 * Below `SIDE_BY_SIDE_MIN_AREA` the area is too narrow to hold both, so the panel overlays and the resize/expand
 * affordances hide themselves — a container query, so it tracks the area rather than the hosting mode (ADR 0017).
 * Esc collapses an expanded panel to docked, else closes. State is page-local (no store). Memoized + remounted
 * per artifact id by the region, so it skips per-token re-renders while a turn streams with the panel open.
 */

import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import type { KeyboardEvent } from "react"
import { Box, Chip, IconButton, Paper, Tooltip, Typography, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { Check, CloseFullscreen, ContentCopy, OpenInFull, Share } from "@mui/icons-material"
import CloseIcon from "@mui/icons-material/Close"
import { useCopyToClipboard } from "hooks/use-copy-to-clipboard"
import { useNotifications } from "hooks/notifications"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { type ArtifactEnvelope, artifactKindLabel, isFileArtifact, sharedArtifactResourceId } from "./artifact-contract"
import { isSkillPackageArtifact } from "./skill-package-artifact"
import { isUnclaimedEscape } from "./escape-key"
import { artifactCardName } from "./artifact-card-name"
import { serializeArtifactData } from "./artifact-serialize"
import { ArtifactPanelBody } from "./artifact-renderers"
import { ArtifactShareDialog } from "./artifact-share-dialog"
import { SIDE_BY_SIDE_MIN_AREA_PX, dockedPanelWidthCss } from "../geometry/panel-metrics"
import { usePanelResize } from "./use-panel-resize"

/**
 * The region's size container. Exported so the query below and the declaration cannot drift apart. The name binds
 * the @container RULE only — `cqi` has no named form and always takes the NEAREST size container, so no container
 * may be introduced between this one and the panel (ADR 0017).
 */
export const AREA_CONTAINER_NAME = "kelly-chat-area"
/**
 * Narrowest area that still holds an artifact and a readable conversation side by side: the sum of what each
 * column measurably needs. Derived rather than picked — its previous value sat above a dock ceiling that no
 * longer exists, and the pair it splits into is what the docked width below is capped against.
 */
const SIDE_BY_SIDE_MIN_AREA = `${SIDE_BY_SIDE_MIN_AREA_PX}px`
const NARROW_AREA_QUERY = `@container ${AREA_CONTAINER_NAME} (max-width: ${SIDE_BY_SIDE_MIN_AREA})`

// z3 clears the composer card's z2 (composer-chrome.tsx). Both are AREA-LOCAL and can never outrank the host's
// chrome: AREA_SX's `isolation: isolate` is what makes that true — `container-type` alone does not (ADR 0017).
// minWidth is degenerate-case insurance: an inline-size container under an intrinsically-sized ancestor computes
// to 0, and a 0-wide overlay is an artifact that opens, takes focus and is invisible.
const OVERLAY_SX = { position: "absolute", inset: 0, zIndex: 3, minWidth: "12rem" } as const
const DOCKED_SX = {
  flexShrink: 0, position: "relative", display: "flex", flexDirection: "column", minHeight: 0, borderLeft: 1, borderColor: "divider",
  // width:auto overrides the inline docked width, which the resize drag also writes imperatively.
  [NARROW_AREA_QUERY]: { ...OVERLAY_SX, width: "auto !important" },
} satisfies SxProps<Theme>
const EXPANDED_SX = { ...OVERLAY_SX, display: "flex", flexDirection: "column", minHeight: 0, borderLeft: 1, borderColor: "divider" } satisfies SxProps<Theme>
const HEADER_SX = { display: "flex", alignItems: "center", gap: 0.5, p: 0.5, overflow: "hidden", borderBottom: 1, borderColor: "divider" } satisfies SxProps<Theme>
const TITLE_SX = { minWidth: 0, ml: 1, fontWeight: 700, color: "primary.main" } satisfies SxProps<Theme>
const KIND_CHIP_SX = { flexShrink: 0, height: 18, fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase" } satisfies SxProps<Theme>
const TITLE_ROW_SX = { flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 } satisfies SxProps<Theme>
const BODY_SX = { flex: 1, minHeight: 0, overflow: "auto", p: 1.5, display: "flex", flexDirection: "column" } satisfies SxProps<Theme>
// Applied only while DOCKED (see the call site): a narrow area has nothing to expand into, but an EXPANDED panel
// still needs its Restore — `isExpanded` survives full→docked, and every docked dock is below this threshold.
const EXPAND_BUTTON_SX = { [NARROW_AREA_QUERY]: { display: "none" } } satisfies SxProps<Theme>

// Left-edge grab strip. Mouse-only (spec-permitted); not focusable, so it never traps keyboard users.
const ResizeHandle = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  width: theme.spacing(0.75),
  transform: "translateX(-50%)",
  cursor: "col-resize",
  zIndex: 2,
  touchAction: "none",
  "&:hover": { backgroundColor: theme.palette.action.hover },
  [NARROW_AREA_QUERY]: { display: "none" },
}))

interface ArtifactPanelProps {
  artifact: ArtifactEnvelope
  artifactName?: string | null
  isExpanded: boolean
  onClose: () => void
  onToggleExpand: () => void
  onEscape: () => void
  /** Focus target when the panel is derived-closed by navigation (unmounts without onClose). */
  fallbackFocusRef: { readonly current: HTMLElement | null }
  width: number
  onWidthChange: (width: number) => void
  /** The chat area the panel docks into — the clamp basis for a drag. */
  containerRef: { readonly current: HTMLElement | null }
  threadId?: string | null
}

function ArtifactPanelComponent({
  artifact,
  artifactName,
  isExpanded,
  onClose,
  onToggleExpand,
  onEscape,
  fallbackFocusRef,
  width,
  onWidthChange,
  containerRef,
  threadId,
}: ArtifactPanelProps) {
  const { copy, isCopied } = useCopyToClipboard()
  const [isShareOpen, setIsShareOpen] = useState(false)
  const notifications = useNotifications()
  const analytics = useAnalytics()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const trackedWidthChange = useCallback((next: number) => {
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_PANEL_RESIZED, { widthPx: next })
    onWidthChange(next)
  }, [analytics, onWidthChange])
  const { panelRef, resizeHandleProps } = usePanelResize(width, trackedWidthChange, containerRef)

  // Move focus into the panel when it opens (and, via the region's key remount, on artifact swap) so focus
  // never drops to <body>; return-to-opener is handled on close.
  useEffect(() => {
    closeButtonRef.current?.focus()
  }, [])

  // A thread switch derives the panel closed and unmounts it WITHOUT onClose, dropping focus to <body>. Restore it
  // to the chat-column fallback — but only when this panel still owns focus at teardown, so an explicit onClose
  // (which already moved focus to the opener before this unmount) doesn't get overridden. Layout-phase so the check
  // runs before the DOM node is detached. panelRef is the Paper root (from usePanelResize).
  useLayoutEffect(() => {
    const panel = panelRef.current
    const fallback = fallbackFocusRef.current // the chat column — a stable sibling node, mounted before this panel
    return () => {
      if (panel != null && panel.contains(document.activeElement)) fallback?.focus()
    }
  }, [panelRef, fallbackFocusRef])

  // Only a copy that actually reached the clipboard reports — a denied permission is not a user taking data away.
  const onCopy = useCallback(async () => {
    if (!await copy(serializeArtifactData(artifact))) return
    notifications.success("Copied to clipboard")
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_COPIED, { artifactId: artifact.id, artifactType: artifact.type })
  }, [copy, artifact, notifications, analytics])

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Don't hijack an Esc a descendant already handled — e.g. cancelling an inline DataGrid cell edit.
      if (!isUnclaimedEscape(event)) return
      // Claim it, or an ancestor Esc handler (the dock's) fires on the same keystroke (ADR 0017).
      event.preventDefault()
      onEscape()
    },
    [onEscape],
  )

  const closeShare = useCallback(() => setIsShareOpen(false), [])

  const copyLabel = isCopied ? "Copied to clipboard" : "Copy data"
  const expandLabel = isExpanded ? "Restore panel size" : "Expand panel"
  const dockedWidth = dockedPanelWidthCss(width)
  const isFile = isFileArtifact(artifact)
  const hasCopyableData = !isFile && !isSkillPackageArtifact(artifact)
  const displayTitle = artifactName ?? artifactCardName(artifact)

  return (
    <Paper
      ref={panelRef}
      square
      elevation={0}
      component="section"
      aria-label="Artifact panel"
      data-testid="chatbox-artifact-panel"
      sx={isExpanded ? EXPANDED_SX : DOCKED_SX}
      style={isExpanded ? undefined : { width: dockedWidth }}
      onKeyDown={onKeyDown}
    >
      {!isExpanded && <ResizeHandle {...resizeHandleProps} data-testid="chatbox-artifact-panel-resize-handle" />}
      <Box sx={HEADER_SX}>
        <Box sx={TITLE_ROW_SX}>
          <Typography variant="subtitle2" noWrap sx={TITLE_SX} title={displayTitle}>
            {displayTitle}
          </Typography>
          <Chip size="small" color="primary" variant="filled" label={artifactKindLabel(artifact)} sx={KIND_CHIP_SX} data-testid="chatbox-artifact-panel-kind" />
        </Box>
        {hasCopyableData && (
          <Tooltip title={copyLabel}>
            <IconButton size="small" aria-label={copyLabel} data-testid="chatbox-artifact-panel-copy" onClick={() => { void onCopy() }}>
              {isCopied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {/* Only file artifacts are shareable: they are the only kind with bytes in S3 to promote.
            For those, Copy is hidden, so Share takes the leading action slot — content actions
            first, window controls trailing. */}
        {isFile && threadId != null && sharedArtifactResourceId(artifact) == null && (
          <Tooltip title="Share artifact">
            <IconButton size="small" aria-label="Share artifact" data-testid="chatbox-artifact-panel-share" onClick={() => setIsShareOpen(true)}>
              <Share fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={expandLabel}>
          <IconButton size="small" aria-label={expandLabel} data-testid="chatbox-artifact-panel-expand" onClick={onToggleExpand} sx={isExpanded ? undefined : EXPAND_BUTTON_SX}>
            {isExpanded ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Close panel">
          <IconButton ref={closeButtonRef} size="small" aria-label="Close panel" data-testid="chatbox-artifact-panel-close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={BODY_SX}>
        <ArtifactPanelBody artifact={artifact} isExpanded={isExpanded} threadId={threadId} />
      </Box>
      {/* Mounted on demand: the dialog owns three queries and a store subscription, and the panel is
          remounted per artifact — a dialog nobody opens should cost none of that. */}
      {isShareOpen && threadId != null && sharedArtifactResourceId(artifact) == null && (
        <ArtifactShareDialog
          open
          onClose={closeShare}
          artifactId={artifact.id}
          artifactName={displayTitle}
          threadId={threadId}
        />
      )}
    </Paper>
  )
}

export const ArtifactPanel = memo(ArtifactPanelComponent)
